import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import useResponsive from "../../../hooks/useResponsive";
import FilterList from "@mui/icons-material/FilterList";
import Grid from "@mui/material/Unstable_Grid2";

interface EventsOverviewFiltersProps {
  displayPastEvents: boolean;
  eventTypeFilter: string[];
  setEventTypeFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setStartMonth: (date: Dayjs | null) => void;
  setEndMonth: (date: Dayjs | null) => void;
  nameFilter: string;
  setNameFilter: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Component that renders the filters for the events overview page
 * @param displayPastEvents boolean that indicates whether past events are displayed, used for conditional rendering of the month filters
 * @param eventTypeFilter the current state of the event type filter
 * @param setEventTypeFilter function to set the state of the event type filter
 * @param renderMonthSwitch function that renders the switch for displaying the months in the event list
 * @param setStartMonth function to set the state of the start month variable that is used for filtering the displayed events
 * @param setEndMonth function to set the state of the end month variable that is used for filtering the displayed events
 * @param nameFilter the current state of the name filter
 * @param setNameFilter function to set the state of the name filter
 * @returns the JSX element for the filters of the events overview page
 */
const EventsOverviewFilters = ({
  displayPastEvents,
  eventTypeFilter,
  setEventTypeFilter,
  setStartMonth,
  setEndMonth,
  nameFilter,
  setNameFilter,
}: EventsOverviewFiltersProps) => {
  const eventTypes = ["WW", "Netzwerk", "JBT goes", "Sonstige", "Workshop", "Pflichtworkshop", "Externer Workshop"];
  const mobile = useResponsive("down", "sm");

  const [startMonthFilter, setStartMonthFilter] = useState<string>(""); // start of month filter field
  const [endMonthFilter, setEndMonthFilter] = useState<string>(""); // end of month filter field

  const isFilterActive =
    eventTypeFilter.length > 0 || startMonthFilter !== "" || endMonthFilter !== "" || nameFilter !== "";

  /**
   * Function that handles the change on the filter field for the event type
   */
  const onChangeEventTypeFilter = (event: SelectChangeEvent<typeof eventTypeFilter>) => {
    const {
      target: { value },
    } = event;
    setEventTypeFilter(typeof value === "string" ? value.split(",") : value);
  };

  /**
   * Function that returns either the start or the end date of a specific month and adds one year if the month is in the past
   * @param month the month for which the date should be returned
   * @param type either start or end
   * @returns the date of the start or end of a month
   */
  function getMonthDate(month: number, type: "start" | "end"): Dayjs {
    const nextYear = dayjs().isAfter(dayjs().month(month).endOf("month"));
    const date =
      type === "start"
        ? nextYear
          ? dayjs().month(month).startOf("month").add(1, "year")
          : dayjs().month(month).startOf("month")
        : nextYear
        ? dayjs().month(month).endOf("month").endOf("d").add(1, "year")
        : dayjs().month(month).endOf("month").endOf("d");
    return date;
  }

  /**
   * Function that sets the start month for the filter
   * Sets the state of the startMonth variable that is responsible for filtering the displayed events
   */
  const onChangeStartMonthFilter = (event: SelectChangeEvent) => {
    switch (event.target.value) {
      case "Januar": {
        setStartMonthFilter("Januar");
        setStartMonth(getMonthDate(0, "start"));
        break;
      }
      case "Februar": {
        setStartMonthFilter("Februar");
        setStartMonth(getMonthDate(1, "start"));
        break;
      }
      case "März": {
        setStartMonthFilter("März");
        setStartMonth(getMonthDate(2, "start"));
        break;
      }
      case "April": {
        setStartMonthFilter("April");
        setStartMonth(getMonthDate(3, "start"));
        break;
      }
      case "Mai": {
        setStartMonthFilter("Mai");
        setStartMonth(getMonthDate(4, "start"));
        break;
      }
      case "Juni": {
        setStartMonthFilter("Juni");
        setStartMonth(getMonthDate(5, "start"));
        break;
      }
      case "Juli": {
        setStartMonthFilter("Juli");
        setStartMonth(getMonthDate(6, "start"));
        break;
      }
      case "August": {
        setStartMonthFilter("August");
        setStartMonth(getMonthDate(7, "start"));
        break;
      }
      case "September": {
        setStartMonthFilter("September");
        setStartMonth(getMonthDate(8, "start"));
        break;
      }
      case "Oktober": {
        setStartMonthFilter("Oktober");
        setStartMonth(getMonthDate(9, "start"));
        break;
      }
      case "November": {
        setStartMonthFilter("November");
        setStartMonth(getMonthDate(10, "start"));
        break;
      }
      case "Dezember": {
        setStartMonthFilter("Dezember");
        setStartMonth(getMonthDate(11, "start"));
        break;
      }
    }
  };

  /**
   * Function that sets the end month for the filter
   * Sets the state of the endMonth variable that is responsible for filtering the displayed events
   */
  const onChangeEndMonthFilter = (event: SelectChangeEvent) => {
    switch (event.target.value) {
      case "Januar": {
        setEndMonthFilter("Januar");
        setEndMonth(getMonthDate(0, "end"));
        break;
      }
      case "Februar": {
        setEndMonthFilter("Februar");
        setEndMonth(getMonthDate(1, "end"));
        break;
      }
      case "März": {
        setEndMonthFilter("März");
        setEndMonth(getMonthDate(2, "end"));
        break;
      }
      case "April": {
        setEndMonthFilter("April");
        setEndMonth(getMonthDate(3, "end"));
        break;
      }
      case "Mai": {
        setEndMonthFilter("Mai");
        setEndMonth(getMonthDate(4, "end"));
        break;
      }
      case "Juni": {
        setEndMonthFilter("Juni");
        setEndMonth(getMonthDate(5, "end"));
        break;
      }
      case "Juli": {
        setEndMonthFilter("Juli");
        setEndMonth(getMonthDate(6, "end"));
        break;
      }
      case "August": {
        setEndMonthFilter("August");
        setEndMonth(getMonthDate(7, "end"));
        break;
      }
      case "September": {
        setEndMonthFilter("September");
        setEndMonth(getMonthDate(8, "end"));
        break;
      }
      case "Oktober": {
        setEndMonthFilter("Oktober");
        setEndMonth(getMonthDate(9, "end"));
        break;
      }
      case "November": {
        setEndMonthFilter("November");
        setEndMonth(getMonthDate(10, "end"));
        break;
      }
      case "Dezember": {
        setEndMonthFilter("Dezember");
        setEndMonth(getMonthDate(11, "end"));
        break;
      }
    }
  };

  // Function that handles the change on the filter field for the event name
  const handleNameFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameFilter(event.target.value);
  };

  /**
   * Function Component that renders the select field for the start month filter
   * @returns the JSX element for the select field for the start month filter
   */
  const SelectStartMonth = () => {
    return (
      <Select label="Monat" value={startMonthFilter} onChange={onChangeStartMonthFilter}>
        <MenuItem value={dayjs().month(0).locale("de").format("MMMM")}>Januar</MenuItem>
        <MenuItem value={dayjs().month(1).locale("de").format("MMMM")}>Februar</MenuItem>
        <MenuItem value={dayjs().month(2).locale("de").format("MMMM")}>März</MenuItem>
        <MenuItem value={dayjs().month(3).locale("de").format("MMMM")}>April</MenuItem>
        <MenuItem value={dayjs().month(4).locale("de").format("MMMM")}>Mai</MenuItem>
        <MenuItem value={dayjs().month(5).locale("de").format("MMMM")}>Juni</MenuItem>
        <MenuItem value={dayjs().month(6).locale("de").format("MMMM")}>Juli</MenuItem>
        <MenuItem value={dayjs().month(7).locale("de").format("MMMM")}>August</MenuItem>
        <MenuItem value={dayjs().month(8).locale("de").format("MMMM")}>September</MenuItem>
        <MenuItem value={dayjs().month(9).locale("de").format("MMMM")}>Oktober</MenuItem>
        <MenuItem value={dayjs().month(10).locale("de").format("MMMM")}>November</MenuItem>
        <MenuItem value={dayjs().month(11).locale("de").format("MMMM")}>Dezember</MenuItem>
      </Select>
    );
  };

  /**
   * Function Component that renders the select field for the end month filter
   * @returns the JSX element for the select field for the end month filter
   */
  const SelectEndMonth = () => {
    return (
      <Select label="Monat" value={endMonthFilter} onChange={onChangeEndMonthFilter}>
        <MenuItem value={dayjs().month(0).locale("de").format("MMMM")}>Januar</MenuItem>
        <MenuItem value={dayjs().month(1).locale("de").format("MMMM")}>Februar</MenuItem>
        <MenuItem value={dayjs().month(2).locale("de").format("MMMM")}>März</MenuItem>
        <MenuItem value={dayjs().month(3).locale("de").format("MMMM")}>April</MenuItem>
        <MenuItem value={dayjs().month(4).locale("de").format("MMMM")}>Mai</MenuItem>
        <MenuItem value={dayjs().month(5).locale("de").format("MMMM")}>Juni</MenuItem>
        <MenuItem value={dayjs().month(6).locale("de").format("MMMM")}>Juli</MenuItem>
        <MenuItem value={dayjs().month(7).locale("de").format("MMMM")}>August</MenuItem>
        <MenuItem value={dayjs().month(8).locale("de").format("MMMM")}>September</MenuItem>
        <MenuItem value={dayjs().month(9).locale("de").format("MMMM")}>Oktober</MenuItem>
        <MenuItem value={dayjs().month(10).locale("de").format("MMMM")}>November</MenuItem>
        <MenuItem value={dayjs().month(11).locale("de").format("MMMM")}>Dezember</MenuItem>
      </Select>
    );
  };

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
      {!displayPastEvents && (
        <Grid xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Von</InputLabel>
              <SelectStartMonth />
            </FormControl>

            <Typography sx={{ alignSelf: "center" }}>-</Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Bis</InputLabel>
              <SelectEndMonth />
            </FormControl>
          </Box>
        </Grid>
      )}

      <Grid xs={12} sm={6} md={displayPastEvents ? 4 : 2}>
        <TextField
          label="Name"
          size="small"
          variant="outlined"
          fullWidth
          value={nameFilter}
          onChange={handleNameFilterChange}
        />
      </Grid>

      <Grid xs={12} sm={6} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Veranstaltungsart</InputLabel>
          <Select
            multiple
            value={eventTypeFilter}
            renderValue={(selected) => selected.join(", ")}
            onChange={onChangeEventTypeFilter}
          >
            {eventTypes.map((type) => (
              <MenuItem key={type} value={type}>
                <Checkbox checked={eventTypeFilter.indexOf(type) > -1} />
                <ListItemText primary={type} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid xs={12} sm={displayPastEvents ? 12 : 6} md={displayPastEvents ? 5 : 3}>
        <Button
          fullWidth
          variant={isFilterActive ? "contained" : "outlined"}
          startIcon={<FilterList />}
          color="secondary"
          sx={{ whiteSpace: "nowrap", height: "100%" }}
          onClick={() => {
            setStartMonthFilter("");
            setEndMonthFilter("");
            setStartMonth(null);
            setEndMonth(null);
            setEventTypeFilter([]);
            setNameFilter("");
          }}
        >
          {mobile ? "Zurücksetzen" : "Filter zurücksetzen"}
        </Button>
      </Grid>
    </Grid>
  );
};

export default EventsOverviewFilters;
